import { useState, useEffect, SyntheticEvent, ChangeEvent } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, useMediaQuery,
  Paper, CircularProgress,
  Divider
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import SearchIcon from '@mui/icons-material/Search';
import { useDrawer } from "../../components/context/DrawerContext";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import PageScrollArea from "../../components/layouts/PageScrollArea";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import CollectFilter from "../../components/filter/collectbyDate";
import { Office } from "../../models/office";
import { Collect } from "../../models/collect";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow } from "../../components/table/interface";
import moment from "moment-timezone";
import { nowOperationalDateString } from "../../utils/operationalDateTime";

// Interfaces
interface Client { id: number; idnumber: string; fullname: string; }
interface Lend {
  id: number;
  date: string;
  date_state: string | null;
  value: number;
  total_value: number;
  current_pay: number;
  client: Client;
  collect?: { id: number; name: string; };
}
interface PayApi {
  id: number;
  pay_date: string;             // "2025-09-01"
  date_operation?: string;      // "2025-09-01T15:23:16.000Z"
  value: number;                // Abono
  value_discount: number;       // Descuento
  lend: Lend;
}
interface RowUI extends SimpleRow {
  id: number;
  collect: string;
  clientName: string;
  loanValue: number;
  pay: number;
  discount: number;
  date: string; // dd/mm/aaaa
  time: string; // HH:mm:ss
}
interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedCollects: string[];
}
interface ReporteDescuentosProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", "America/Bogota")
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", "America/Bogota")
    : moment.tz(dateString, "America/Bogota");
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};
const formatTime = (iso?: string): string => {
  if (!iso) return "";
  const parsed = moment.tz(iso, "America/Bogota");
  return parsed.isValid() ? parsed.format("HH:mm:ss") : "";
};

// Estilos
const SummaryBox = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[0],
  border: `1px solid ${theme.palette.divider}`,
}));

// Componente
const ReporteDescuentos: React.FC<ReporteDescuentosProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  document.title = "Reporte Descuento";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const { drawerOpen } = useDrawer();

  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });

  // datos UI
  const [rows, setRows] = useState<RowUI[]>([]);
  const [summary, setSummary] = useState({ totalPayments: 0, totalDiscount: 0, count: 0 });

  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar las oficinas.", "error");
      }
    })();
  }, [showAlert]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/collectsoffice/${filter.officeId}`);
        setCollectsOffice(data || []);
        setFilter((prev) => ({ ...prev, selectedCollects: [] }));
        setSelectAll(false);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar los cobros para la oficina.", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert]);

  // handlers
  const setStartDate = (value: string) => {
    setFilter((prev) => ({ ...prev, startDate: value }));
  };
  const setEndDate = (value: string) => {
    setFilter((prev) => ({ ...prev, endDate: value }));
  };
  const setOfficeId = (value: string) => {
    setFilter((prev) => ({ ...prev, officeId: value }));
  };

  const updateSelectedCollects = (updater: (prevSelected: string[]) => string[]) => {
    setFilter((prev) => {
      const nextSelected = updater(prev.selectedCollects);
      setSelectAll(nextSelected.length === collectsOffice.length && collectsOffice.length > 0);
      return { ...prev, selectedCollects: nextSelected };
    });
  };

  const handleSelectAllCollects = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    updateSelectedCollects(() =>
      checked ? collectsOffice.map((collect) => collect.id.toString()) : []
    );
  };

  const handleCollectChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    updateSelectedCollects((prevSelected) => {
      const selected = new Set(prevSelected);
      if (checked) selected.add(value); else selected.delete(value);
      return Array.from(selected);
    });
  };

  const handleSetSelectedCollects = (values: string[]) => {
    updateSelectedCollects(() => values);
  };

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert("Seleccione una oficina.", "warning");
      return false;
    }
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro.", "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert("La fecha de inicio no puede ser mayor a la fecha final.", "warning");
      return false;
    }
    return true;
  };

  // Columnas EXACTAS a la tabla de la imagen
  const columns: SimpleColumn[] = [
    { field: "collect", headerName: "Cobro", minWidth: 150 },
    { field: "clientName", headerName: "Cliente", minWidth: 260, wrapText: true },
    {
      field: "loanValue",
      headerName: "Valor Prestamo",
      minWidth: 150,
      align: "right",
      renderCell: ({ row }) => (
        <span className="currency-cell">{formatCurrency(row.loanValue || 0)}</span>
      ),
    },
    {
      field: "pay",
      headerName: "Abono",
      minWidth: 120,
      align: "right",
      renderCell: ({ row }) => (
        <span className="currency-cell">{formatCurrency(row.pay || 0)}</span>
      ),
    },
    {
      field: "discount",
      headerName: "Descuento",
      minWidth: 120,
      align: "right",
      renderCell: ({ row }) => (
        <span className="currency-cell">{formatCurrency(row.discount || 0)}</span>
      ),
    },
    { field: "date", headerName: "Fecha", minWidth: 120, align: "center" },
    { field: "time", headerName: "Hora", minWidth: 110, align: "center" },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    try {
      const collectIds = filter.selectedCollects
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));

      const payload = {
        start_date: filter.startDate,                  // "YYYY-MM-DD"
        end_date: filter.endDate,                      // "YYYY-MM-DD"
        collect_Id: collectIds,                        // [ids]
      };

      // Endpoint correcto
      const { data } = await axios.post("/inform/discountpay", payload);
      const pays: PayApi[] = data?.data || [];

      if (!pays.length) {
        setRows([]);
        setSummary({ totalPayments: 0, totalDiscount: 0, count: 0 });
        showAlert("No se encontraron registros en el período.", "info");
        return;
      }

      const rowsMapped: RowUI[] = pays.map((p) => ({
        id: p.id,
        collect: p?.lend?.collect?.name || "",
        clientName: p?.lend?.client?.fullname || "",
        loanValue: p?.lend?.total_value ?? 0,
        pay: p?.value ?? 0,
        discount: p?.value_discount ?? 0,
        date: formatDate(p?.pay_date),
        time: formatTime(p?.date_operation),
      }));

      const totalPayments = rowsMapped.reduce((acc, r) => acc + (r.pay || 0), 0);
      const totalDiscount = rowsMapped.reduce((acc, r) => acc + (r.discount || 0), 0);

      setRows(rowsMapped);
      setSummary({ totalPayments, totalDiscount, count: rowsMapped.length });
      showAlert(`Se encontraron ${rowsMapped.length} registros.`, "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert("Error al obtener los datos.", "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Reporte Descuento</Title>
        <Box>
          <Button type="submit" variant="contained" startIcon={!isMobile && <SearchIcon />} form="form-descuentos">
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>

      <Card sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', overflow: "hidden", display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <form id="form-descuentos" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <CollectFilter
              start_date={filter.startDate}
              end_date={filter.endDate}
              office_id={filter.officeId}
              setStart_date={setStartDate}
              setEnd_date={setEndDate}
              setOffice_id={setOfficeId}
              offices={offices}
              collectsOffice={collectsOffice}
              selectedCollects={filter.selectedCollects}
              setSelectedCollects={handleSetSelectedCollects}
              selectAll={selectAll}
              setSelectAll={setSelectAll}
              handleCheckboxChange={handleCollectChange}
              handleSelectAll={handleSelectAllCollects}
            />
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent sx={{ pt: 0 }}>
          <PageScrollArea sx={{ height: 380 }}>
            {rows.length > 0 ? (
              <>
                <SimpleCustomTable
                  rows={rows}
                  columns={columns}
                  showBorders
                  maxHeight={500}
                  showCount
                  exportCsv
                  exportFileName="descuento-fecha.csv"
                  wrapCellContent
                />
              </>
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
          </PageScrollArea>
        </CardContent>
      </Card>

      {/* Loader overlay opcional (no desmonta) */}
      {loading && (
        <Box sx={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.12)', zIndex: 2000
        }}>
          <CircularProgress />
        </Box>
      )}
    </HeaderResponsive>
  );
};

export default ReporteDescuentos;
